/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from '../../help/sample_pages/ActionCreatePage';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { analyzeActionDelete } from './ActionDeleteAnalyze';
import { defaultValue } from './ActionDeleteCurrentRecord';

const ActionDeleteCurrentRecordManager: ActionManagerFn<
  'ActionDeleteCurrentRecord'
> = (platform: Platform): ActionManager<'ActionDeleteCurrentRecord'> => {
  return {
    ...baseActionManager(platform, 'ActionDeleteCurrentRecord'),
    defaultValue,
    props: {
      ...baseActionProps,
      alias: {
        label: 'Alias',
        type: 'DataSourceAliasChooser',
        required: true,
        allowedTypes: ['DataStore', 'LocalDataStore', 'TransientDataStore'],
      },
      confirmationTitle: {
        label: 'Confirmation Dialog Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      confirmationMessage: {
        label: 'Confirmation Message',
        type: 'expression',
        returnType: async () => 'string',
        helpText: 'Leave blank to skip the confirmation dialog',
      },
    },
    name: 'Mark Current Record for Deletion',
    getDisplayText: (item) => item.props.alias,
    isDataSourceAliasUsed: (item, alias) => item.props.alias === alias,
    icon: { iconName: 'trash' },
    analyze: analyzeActionDelete,
    description: `**Overview**<br/>
The "Mark Current Record for Deletion" action is a data state management component designed to flag a specific record within a Data Store for removal. Crucially, this action does not immediately delete the record from the physical database; instead, it marks the record locally as "deleted." This allows for batch operations or review before changes are committed.<br/><br/>
To permanently finalize the deletion in the backend database, this action must be followed by an Action Save or Action Save Transaction. The component operates on the "current record" context, typically the selected row in a grid or the active record in a form. It includes built-in provisions for user confirmation, allowing developers to interrupt the flow with a verification dialog before the mark is applied.

**Key Features**<br/>
* **Deferred Deletion Logic:** Explicitly separates "marking" a deletion from "saving" the deletion. This ensures data integrity and supports transactional workflows where multiple changes are committed simultaneously.
* **Integrated User Confirmation:** Features native support for a confirmation dialog via confirmationTitle and confirmationMessage properties. If the message is left blank, the dialog is skipped.
* **Dynamic Feedback:** Both confirmation properties support expression types, enabling dynamic messages (e.g., "Delete user John Doe?") that reference runtime data variables.
* **Store Compatibility:** Supports DataStore, LocalDataStore, and TransientDataStore, making it versatile for both persistent and temporary data management.

**Properties**<br/>
* **Alias (Required):** A DataSourceAliasChooser property identifying the Data Store containing the record to be marked.
* **Confirmation Dialog Title:** An expression property (returning a string) that defines the header of the pop-up dialog.
* **Confirmation Message:** An expression property (returning a string) that defines the body text of the pop-up dialog.
  * *Behavior:* If this property is empty, the confirmation step is bypassed, and the record is marked immediately.

**Use Cases**<br/>
* **Grid Row Removal:** Adding a "Trash" icon column in a data grid that, when clicked, asks "Are you sure?" before flagging that specific row for removal.
* **Form Cancellation:** Removing a temporary entry from a TransientDataStore (e.g., a drafted line item) before the main form is submitted.`,
    helpRenders: [
      {
        title: 'Action Mark Current Record for Deletion',
        description: `Select a record from the below grid and press the Invoke Action button to see it in action.
            
\`\`\`alert
Note: This action will just mark the record for deletion, you need to use Action Save or Save Trasaction to persist to the database.
\`\`\``,
        page: SamplePage,
        height: 300,
        exampleId: 'e1',
        clearParentChildren: true,
        visibleProps: [],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
      {
        title: 'User Confirmation',
        description:
          'If the Confirmation Message prop is set with some value, then a user confirmation dialog will be shown confirming the delete, given user an option to cancel the action.',
        page: SamplePage,
        height: 300,
        exampleId: 'e2',
        clearParentChildren: true,
        visibleProps: ['confirmationTitle', 'confirmationMessage'],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
            confirmationMessage: { type: 'text', value: 'Are you sure?' },
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
      {
        title: 'Dynamic Confirmation Message',
        description:
          'Use expression to show exactly what the user is trying to delete.',
        page: SamplePage,
        height: 300,
        exampleId: 'e3',
        clearParentChildren: true,
        visibleProps: ['confirmationTitle', 'confirmationMessage'],
        item: {
          children: [],
          itemId: 'actionDelete',
          parent: 'triggerOnClick',
          props: {
            alias: 'test',
            confirmationTitle: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Delete ${$.test.name}?`',
            },
            confirmationMessage: {
              type: 'expression',
              // eslint-disable-next-line no-template-curly-in-string
              value: '`Are you sure, you want to delete ${$.test.name}?`',
            },
          },
          type: 'ActionDeleteCurrentRecord',
        },
      },
    ],
  };
};

export default ActionDeleteCurrentRecordManager;
